import type { Brand } from "@pdfking/tfunc"
import type { PdfiumCTypes, PdfiumSchema } from "@pdfking/pdfium"

import type { Pointer } from "./Pointer"

export type { PdfiumSchema }

// Compile-time check: PdfiumTypeMap keys must match PdfiumCTypes exactly
type AssertExact<T, U> = [T] extends [U]
  ? [U] extends [T]
    ? true
    : false
  : false
type _CheckTypeMap =
  AssertExact<keyof PdfiumTypeMap, PdfiumCTypes> extends true
    ? true
    : "ERROR: PdfiumTypeMap keys don't match PdfiumCTypes"
export const _typeMapCheck: _CheckTypeMap = true

export type PdfiumTypeMap = {
  FPDFANNOT_COLORTYPE: number
  FPDF_ACTION: Pointer
  FPDF_ANNOTATION: Pointer
  "FPDF_ANNOTATION *": Pointer
  FPDF_ANNOTATION_SUBTYPE: Pointer
  "FPDF_ANNOTATION_SUBTYPE *": Pointer
  FPDF_ANNOT_APPEARANCEMODE: Pointer
  FPDF_ATTACHMENT: Pointer
  FPDF_AVAIL: Brand<Pointer, "FPDF_AVAIL">
  FPDF_BITMAP: Brand<Pointer, "FPDF_BITMAP">
  FPDF_BOOKMARK: Pointer
  FPDF_BOOL: number
  "FPDF_BOOL *": Pointer
  FPDF_BYTESTRING: Pointer
  FPDF_CLIPPATH: Pointer
  "FPDF_COLORSCHEME *": Pointer
  "FPDF_CharsetFontMap *": Pointer
  FPDF_DEST: Pointer
  FPDF_DOCUMENT: Brand<Pointer, "FPDF_DOCUMENT">
  FPDF_DUPLEXTYPE: Pointer
  FPDF_DWORD: number
  "FPDF_FILEACCESS *": Pointer
  FPDF_FILEIDTYPE: Pointer
  "FPDF_FILEWRITE *": Pointer
  FPDF_FONT: Pointer
  "FPDF_FORMFILLINFO *": Pointer
  FPDF_FORMHANDLE: Pointer
  FPDF_GLYPHPATH: Pointer
  "FPDF_IMAGEOBJ_METADATA *": Pointer
  FPDF_JAVASCRIPT_ACTION: Pointer
  "FPDF_LIBRARY_CONFIG *": Pointer
  FPDF_LINK: Pointer
  "FPDF_LINK *": Pointer
  FPDF_OBJECT_TYPE: Pointer
  FPDF_PAGE: Brand<Pointer, "FPDF_PAGE">
  "FPDF_PAGE *": Pointer
  FPDF_PAGELINK: Pointer
  FPDF_PAGEOBJECT: Pointer
  FPDF_PAGEOBJECTMARK: Pointer
  FPDF_PAGERANGE: Pointer
  FPDF_PATHSEGMENT: Pointer
  FPDF_SCHHANDLE: Pointer
  FPDF_SIGNATURE: Pointer
  FPDF_STRING: Pointer
  FPDF_STRUCTELEMENT: Pointer
  FPDF_STRUCTELEMENT_ATTR: Pointer
  FPDF_STRUCTELEMENT_ATTR_VALUE: Pointer
  FPDF_STRUCTTREE: Pointer
  "FPDF_SYSFONTINFO *": Pointer
  FPDF_TEXTPAGE: Pointer
  FPDF_TEXT_RENDERMODE: Pointer
  "FPDF_WCHAR *": Pointer
  FPDF_WIDESTRING: Pointer
  FPDF_XOBJECT: Pointer
  "FS_FLOAT *": Pointer
  "FS_MATRIX *": Pointer
  "FS_POINTF *": Pointer
  "FS_QUADPOINTSF *": Pointer
  "FS_RECTF *": Pointer
  "FS_SIZEF *": Pointer
  "FX_DOWNLOADHINTS *": Pointer
  "FX_FILEAVAIL *": Pointer
  "IFSDK_PAUSE *": Pointer
  "char *": Pointer
  double: number
  "double *": Pointer
  float: number
  "float *": Pointer
  int: number
  "int *": Pointer
  "long *": Pointer
  size_t: number
  "size_t *": Pointer
  uint32_t: number
  "uint32_t *": Pointer
  "uint8_t *": Pointer
  "unsigned char": number
  "unsigned char *": Pointer
  "unsigned int": number
  "unsigned int *": Pointer
  "unsigned long": number
  "unsigned long *": Pointer
  "unsigned short *": Pointer
  void: void
  "void *": Pointer
}

type MapArgs<T extends readonly string[]> = {
  [I in keyof T]: T[I] extends keyof PdfiumTypeMap ? PdfiumTypeMap[T[I]] : never
}

export type PdfiumFunctions = keyof PdfiumSchema

export type PdfiumArguments = {
  readonly [K in PdfiumFunctions]: MapArgs<PdfiumSchema[K][0]>
}

export type PdfiumReturns = {
  readonly [K in PdfiumFunctions]: PdfiumSchema[K][1] extends keyof PdfiumTypeMap
    ? PdfiumTypeMap[PdfiumSchema[K][1]]
    : never
}
